import { useState, useCallback } from 'react';
import { Credentials } from '@app/shared/types';

interface CredentialsState {
  newCredentials: Credentials[];
  editedCredentials: Record<string, Credentials>;
  deletedCredentials: Set<string>;
  hasChanges: boolean;
}

interface UseCredentialsStateProps {
  initialData?: {
    dxm: Credentials[];
    external: Credentials[];
  };
}

export const useCredentialsState = ({
  initialData,
}: UseCredentialsStateProps) => {
  const [state, setState] = useState<CredentialsState>({
    newCredentials: [],
    editedCredentials: {},
    deletedCredentials: new Set(),
    hasChanges: false,
  });

  const resetState = useCallback(() => {
    setState({
      newCredentials: [],
      editedCredentials: {},
      deletedCredentials: new Set(),
      hasChanges: false,
    });
  }, []);

  const addCredentials = useCallback(() => {
    setState(prev => ({
      ...prev,
      newCredentials: [
        ...prev.newCredentials,
        { name: '', clientId: '', clientSecret: '', project: '', env: '' },
      ],
      hasChanges: true,
    }));
  }, []);

  const handleDeleteNew = useCallback((index: number) => {
    setState(prev => {
      const newCredentials = prev.newCredentials.filter((_, i) => i !== index);
      return {
        ...prev,
        newCredentials,
        hasChanges:
          newCredentials.length > 0 ||
          Object.keys(prev.editedCredentials).length > 0 ||
          prev.deletedCredentials.size > 0,
      };
    });
  }, []);

  const handleDeleteExisting = useCallback((clientId: string) => {
    setState(prev => {
      const newEditedCredentials = { ...prev.editedCredentials };
      const newDeletedCredentials = new Set(prev.deletedCredentials);

      newDeletedCredentials.add(clientId);
      delete newEditedCredentials[clientId];

      return {
        ...prev,
        editedCredentials: newEditedCredentials,
        deletedCredentials: newDeletedCredentials,
        hasChanges: true,
      };
    });
  }, []);

  const handleUpdateNew = useCallback((index: number, data: Credentials) => {
    setState(prev => ({
      ...prev,
      newCredentials: prev.newCredentials.map((cred, i) =>
        i === index ? data : cred,
      ),
      hasChanges: true,
    }));
  }, []);

  const handleUpdateExisting = useCallback(
    (clientId: string, data: Credentials) => {
      setState(prev => ({
        ...prev,
        editedCredentials: {
          ...prev.editedCredentials,
          [clientId]: data,
        },
        hasChanges: true,
      }));
    },
    [],
  );

  const validateCredentials = useCallback(() => {
    const newCredentialsValid = state.newCredentials.every(
      cred =>
        cred.name?.trim() && cred.clientId?.trim() && cred.clientSecret?.trim(),
    );
    const editedCredentialsValid = Object.values(state.editedCredentials).every(
      cred =>
        cred.name?.trim() && cred.clientId?.trim() && cred.clientSecret?.trim(),
    );

    return newCredentialsValid && editedCredentialsValid;
  }, [state.newCredentials, state.editedCredentials]);

  const prepareDataForSave = useCallback(() => {
    if (!initialData) return [];
    const remainingExternalCredentials = initialData.external.filter(
      cred => !state.deletedCredentials.has(cred.clientId),
    );

    const finalExternalCredentials = remainingExternalCredentials.map(
      cred => state.editedCredentials[cred.clientId] || cred,
    );

    return Array.from(
      new Set([...finalExternalCredentials, ...state.newCredentials]),
    );
  }, [initialData, state]);

  return {
    state,
    resetState,
    addCredentials,
    handleDeleteNew,
    handleDeleteExisting,
    handleUpdateNew,
    handleUpdateExisting,
    validateCredentials,
    prepareDataForSave,
  };
};
